import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { CheckOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { IPipelineStage, StageStatus } from '../../../core/modules/pipelines/pipelines.model';
import { formatDuration } from '../utils/formatStage';
import {
  STAGE_STATUS_HEX as STATUS_COLOR,
  STAGE_STATUS_LABEL as STATUS_LABEL
} from '../utils/statusColors';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.5); }
  50% { box-shadow: 0 0 0 6px rgba(24, 144, 255, 0); }
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
  padding: 8px 4px 4px;
`;

const Row = styled.div`
  display: flex;
  align-items: stretch;
  min-width: max-content;
`;

const Cell = styled.div<{ $isFirst: boolean; $isLast: boolean }>`
  flex: 1;
  min-width: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  padding: 0 4px;
`;

const TrackRow = styled.div`
  position: relative;
  width: 100%;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Track = styled.div<{
  $color: string;
  $side: 'left' | 'right';
  $hide: boolean;
  $dashed: boolean;
}>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 0; right: 50%;' : 'left: 50%; right: 0;')}
  height: 0;
  border-top: 2px ${({ $dashed }) => ($dashed ? 'dashed' : 'solid')} ${({ $color }) => $color};
  transform: translateY(-50%);
  visibility: ${({ $hide }) => ($hide ? 'hidden' : 'visible')};
`;

const MidDot = styled.span<{ $color: string; $side: 'left' | 'right'; $hide: boolean }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 25%;' : 'left: 75%;')}
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fff;
  border: 1.5px solid ${({ $color }) => $color};
  visibility: ${({ $hide }) => ($hide ? 'hidden' : 'visible')};
`;

const Circle = styled.div<{ $status: StageStatus }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $status }) => ($status === 'not-started' || $status === 'skipped'
    ? '#fff'
    : STATUS_COLOR[$status])};
  border: 2px solid ${({ $status }) => STATUS_COLOR[$status]};
  color: ${({ $status }) => ($status === 'not-started' || $status === 'skipped'
    ? STATUS_COLOR[$status]
    : '#fff')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
  z-index: 1;
  position: relative;
  animation: ${({ $status }) => ($status === 'running' ? pulse : 'none')} 1.6s infinite;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
  padding: 0 6px;
`;

const Name = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const StatusLabel = styled.span<{ $status: StageStatus }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $status }) => STATUS_COLOR[$status]};
`;

const Duration = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  font-variant-numeric: tabular-nums;
`;

const ElapsedBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #1890ff;
  font-variant-numeric: tabular-nums;
`;

const useElapsedSec = (startedAt: string | null | undefined, active: boolean): number | null => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const id = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(id);
  }, [active]);

  if (!active || !startedAt) {
    return null;
  }

  const ms = now - new Date(startedAt).getTime();

  return Math.max(0, Math.round(ms / 1000));
};

const ChildList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  margin-top: 8px;
`;

const ChildBadge = styled.span<{ $color: string }>`
  font-size: 10px;
  padding: 1px 7px;
  border-radius: 9px;
  background: #fff;
  border: 1px solid ${({ $color }) => $color}40;
  color: ${({ $color }) => $color};
  font-weight: 500;
`;

const circleContent = (status: StageStatus, fallback: number) => {
  if (status === 'success') {
    return <CheckOutlined style={{ fontSize: 14 }} />;
  }

  if (status === 'failed') {
    return <CloseOutlined style={{ fontSize: 14 }} />;
  }

  if (status === 'running') {
    return <LoadingOutlined spin style={{ fontSize: 14 }} />;
  }

  return fallback;
};

const trackColor = (status: StageStatus | null): string => {
  if (!status) {
    return '#d9d9d9';
  }

  if (status === 'success' || status === 'failed' || status === 'running') {
    return STATUS_COLOR[status];
  }

  return '#d9d9d9';
};

const trackDashed = (status: StageStatus | null): boolean => (
  !status || status === 'not-started' || status === 'skipped'
);

interface IHorizontalStagesProps {
  stages: IPipelineStage[];
}

const StageCell: React.FC<{
  stage: IPipelineStage;
  idx: number;
  total: number;
  prev: IPipelineStage | null;
  next: IPipelineStage | null;
  pipelineStartedAt?: string | null;
}> = ({ stage, idx, total, prev, next, pipelineStartedAt }) => {
  const leftColor = trackColor(prev?.status ?? null);
  const rightColor = trackColor(stage.status);
  const leftDashed = trackDashed(prev?.status ?? null);
  const rightDashed = trackDashed(stage.status);

  const elapsedSec = useElapsedSec(
    stage.startedAt ?? pipelineStartedAt ?? null,
    stage.status === 'running'
  );

  return (
    <Cell $isFirst={idx === 0} $isLast={idx === total - 1}>
      <TrackRow>
        <Track $color={leftColor} $side='left' $hide={idx === 0} $dashed={leftDashed} />
        <Track
          $color={rightColor}
          $side='right'
          $hide={idx === total - 1}
          $dashed={rightDashed}
        />
        <MidDot $color={leftColor} $side='left' $hide={idx === 0} />
        <MidDot $color={rightColor} $side='right' $hide={idx === total - 1 || !next} />
        <Circle $status={stage.status}>{circleContent(stage.status, idx + 1)}</Circle>
      </TrackRow>
      <Body>
        <Name>{stage.name}</Name>
        <StatusLabel $status={stage.status}>{STATUS_LABEL[stage.status]}</StatusLabel>
        {stage.status === 'running' && elapsedSec !== null && (
          <ElapsedBadge>{formatDuration(elapsedSec)} elapsed</ElapsedBadge>
        )}
        {stage.status !== 'running' && typeof stage.durationSec === 'number' && (
          <Duration>{formatDuration(stage.durationSec)}</Duration>
        )}
        {stage.children && stage.children.length > 0 && (
          <ChildList>
            {stage.children.map((child) => (
              <ChildBadge key={child.id} $color={STATUS_COLOR[child.status]}>
                {child.name}
              </ChildBadge>
            ))}
          </ChildList>
        )}
      </Body>
    </Cell>
  );
};

interface IHorizontalStagesPropsExt extends IHorizontalStagesProps {
  pipelineStartedAt?: string | null;
}

const HorizontalStages: React.FC<IHorizontalStagesPropsExt> = ({ stages, pipelineStartedAt }) => {
  if (!stages || stages.length === 0) {
    return <em style={{ color: 'rgba(0,0,0,0.45)' }}>No stages reported</em>;
  }

  return (
    <ScrollContainer>
      <Row>
        {stages.map((stage, idx) => (
          <StageCell
            key={stage.id}
            stage={stage}
            idx={idx}
            total={stages.length}
            prev={idx > 0 ? stages[idx - 1] : null}
            next={idx < stages.length - 1 ? stages[idx + 1] : null}
            pipelineStartedAt={pipelineStartedAt}
          />
        ))}
      </Row>
    </ScrollContainer>
  );
};

export default HorizontalStages;
